import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';

type PeriodType = 'last_30_days' | 'last_90_days' | 'last_6_months' | 'last_12_months' | 'custom';

interface DateRange {
  start: Date;
  end: Date;
}

interface ReportingPeriodContextValue {
  period: PeriodType;
  startDate: Date | null;
  endDate: Date | null;
  setPeriod: (period: PeriodType) => void;
  setCustomRange: (start: Date, end: Date) => void;
  dateRange: DateRange;
  queryKeyPart: string;
}

const ReportingPeriodContext = createContext<ReportingPeriodContextValue | undefined>(undefined);

const STORAGE_KEY = 'reporting-period-settings';

interface StoredSettings {
  period: PeriodType;
  startDate: string | null;
  endDate: string | null;
}

/**
 * Calculate date range based on period type
 */
function calculateDateRange(
  period: PeriodType,
  customStart: Date | null,
  customEnd: Date | null
): DateRange {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (period) {
    case 'last_30_days': {
      const start = new Date(today);
      start.setDate(start.getDate() - 30);
      return { start, end: today };
    }
    case 'last_90_days': {
      const start = new Date(today);
      start.setDate(start.getDate() - 90);
      return { start, end: today };
    }
    case 'last_6_months': {
      const start = new Date(today);
      start.setMonth(start.getMonth() - 6);
      return { start, end: today };
    }
    case 'last_12_months': {
      const start = new Date(today);
      start.setMonth(start.getMonth() - 12);
      return { start, end: today };
    }
    case 'custom': {
      if (customStart && customEnd) {
        return { start: customStart, end: customEnd };
      }
      // Fallback to last 30 days if custom dates not set
      const start = new Date(today);
      start.setDate(start.getDate() - 30);
      return { start, end: today };
    }
  }
}

/**
 * Generate query key part for TanStack Query integration
 */
function generateQueryKeyPart(
  period: PeriodType,
  startDate: Date | null,
  endDate: Date | null
): string {
  switch (period) {
    case 'last_30_days':
      return '30d';
    case 'last_90_days':
      return '90d';
    case 'last_6_months':
      return '6m';
    case 'last_12_months':
      return '12m';
    case 'custom': {
      if (startDate && endDate) {
        const formatDate = (date: Date) => date.toISOString().split('T')[0];
        return `custom_${formatDate(startDate)}_${formatDate(endDate)}`;
      }
      return 'custom';
    }
  }
}

/**
 * Load settings from localStorage
 */
function loadSettings(): StoredSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
  // Error handled silently
  }

  return {
    period: 'last_30_days',
    startDate: null,
    endDate: null,
  };
}

/**
 * Save settings to localStorage
 */
function saveSettings(settings: StoredSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
  // Error handled silently
  }
}

interface ReportingPeriodProviderProps {
  children: ReactNode;
}

/**
 * Provider component that manages global reporting period state
 * Provides date range selection for reports and analytics across the app
 */
export function ReportingPeriodProvider({ children }: ReportingPeriodProviderProps) {
  const [period, setPeriodState] = useState<PeriodType>('last_30_days');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isInitialised, setIsInitialised] = useState(false);

  // Load settings from localStorage on mount
  useEffect(() => {
    const settings = loadSettings();
    setPeriodState(settings.period);

    if (settings.startDate) {
      setStartDate(new Date(settings.startDate));
    }
    if (settings.endDate) {
      setEndDate(new Date(settings.endDate));
    }

    setIsInitialised(true);
  }, []);

  // Save settings to localStorage when they change
  useEffect(() => {
    if (!isInitialised) return;

    const settings: StoredSettings = {
      period,
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
    };

    saveSettings(settings);
  }, [period, startDate, endDate, isInitialised]);

  const setPeriod = (newPeriod: PeriodType) => {
    setPeriodState(newPeriod);

    // Clear custom dates when switching to preset periods
    if (newPeriod !== 'custom') {
      setStartDate(null);
      setEndDate(null);
    }
  };

  const setCustomRange = (start: Date, end: Date) => {
    setPeriodState('custom');
    setStartDate(start);
    setEndDate(end);
  };

  const dateRange = useMemo(
    () => calculateDateRange(period, startDate, endDate),
    [period, startDate, endDate]
  );

  const queryKeyPart = useMemo(
    () => generateQueryKeyPart(period, startDate, endDate),
    [period, startDate, endDate]
  );

  const value: ReportingPeriodContextValue = {
    period,
    startDate,
    endDate,
    setPeriod,
    setCustomRange,
    dateRange,
    queryKeyPart,
  };

  return (
    <ReportingPeriodContext.Provider value={value}>
      {children}
    </ReportingPeriodContext.Provider>
  );
}

/**
 * Hook to access reporting period context
 * Throws error if used outside of ReportingPeriodProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useReportingPeriod(): ReportingPeriodContextValue {
  const context = useContext(ReportingPeriodContext);

  if (context === undefined) {
    throw new Error('useReportingPeriod must be used within a ReportingPeriodProvider');
  }

  return context;
}
